import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { awsConfig } from "../aws-config";
import { getAuthHeader } from "../api";

interface MatchedPro {
  bookingId: string;
  proId: string;
  proName: string;
  proRating: number;
  proPhoto: string;
  serviceCategory: string;
  experience: string;
  city: string;
  hourlyRate: number;
  bio: string;
  isOnline?: boolean;
  hireCount?: number;
  acceptanceRate?: number;
}

export default function ProSelectionPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatchId = searchParams.get("dispatchId") || "";

  const [pros, setPros] = useState<MatchedPro[]>([]);
  const [loading, setLoading] = useState(true);
  const [selecting, setSelecting] = useState<string | null>(null);
  const [selected, setSelected] = useState(false);
  const [selectedProName, setSelectedProName] = useState("");
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [timeLeft, setTimeLeft] = useState("");
  const [expired, setExpired] = useState(false);

  useEffect(() => { loadMatches(); }, []);

  useEffect(() => {
    if (!expiresAt) return;
    const interval = setInterval(() => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      if (diff <= 0) { setTimeLeft(""); setExpired(true); clearInterval(interval); return; }
      const hours = Math.floor(diff / 3600000);
      const mins = Math.floor((diff % 3600000) / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(hours > 0 ? `${hours}h ${mins}m ${secs}s` : `${mins}m ${secs}s`);
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  async function loadMatches() {
    try {
      const headers = await getAuthHeader();
      const response = await axios.get(`${awsConfig.apiUrl}/bookings/matches?dispatchId=${dispatchId}`, { headers });
      setPros(response.data.matches || []);
      setExpiresAt(response.data.expiresAt || "");
    } catch (err) { console.error("Failed to load matches:", err); }
    finally { setLoading(false); }
  }

  async function handleSelect(bookingId: string, proName: string) {
    setSelecting(bookingId);
    try {
      const headers = await getAuthHeader();
      await axios.post(`${awsConfig.apiUrl}/dispatch/select`, { bookingId, dispatchId }, { headers });
      setSelectedProName(proName);
      setSelected(true);
    } catch (err) { console.error("Failed to select pro:", err); }
    finally { setSelecting(null); }
  }

  // Urgency color based on time left
  const timerColor = timeLeft.includes("m") && !timeLeft.includes("h") && parseInt(timeLeft) <= 5
    ? "text-red-500 bg-red-50"
    : "text-yellow-700 bg-yellow-50";

  if (selected) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-5">
      <div className="text-center animate-slide-up max-w-sm">
        <div className="w-24 h-24 bg-teal-light rounded-full flex items-center justify-center text-5xl mx-auto mb-4">✅</div>
        <h1 className="font-display text-2xl font-bold text-navy mb-2">Great Choice!</h1>
        <p className="text-sm text-gray-500 mb-1">
          <span className="font-bold text-navy">{selectedProName}</span> has been notified.
        </p>
        <p className="text-xs text-gray-400 mb-6">They'll confirm your booking shortly.</p>
        <div className="space-y-3">
          <button onClick={() => navigate("/bookings")} className="btn btn-primary btn-full btn-lg">
            View My Bookings
          </button>
          <button onClick={() => navigate("/home")} className="btn btn-secondary btn-full">
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white px-5 pt-5 pb-4 sticky top-0 z-20 border-b border-gray-100">
        <button onClick={() => navigate(-1)}
          className="text-gray-400 text-sm font-semibold mb-3 flex items-center gap-1">
          ← Back
        </button>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold text-navy">Choose Your Pro</h1>
            <p className="text-xs text-gray-400 mt-0.5">
              {loading ? "Finding matches..." : `${pros.length} pros matched for your job`}
            </p>
          </div>
          {timeLeft && (
            <div className={`rounded-xl px-3 py-2 flex items-center gap-1.5 flex-shrink-0 ${timerColor}`}>
              <span className="text-sm">⏱</span>
              <span className="text-xs font-bold">{timeLeft}</span>
            </div>
          )}
        </div>

        {/* Progress indicator */}
        {!loading && pros.length > 0 && (
          <div className="mt-3 flex gap-1.5">
            {pros.map((_, i) => (
              <div key={i} className="flex-1 h-1 bg-mouna-blue rounded-full opacity-30"
                style={{opacity: i === 0 ? 1 : 0.3}}/>
            ))}
          </div>
        )}
      </div>

      <div className="px-5 pt-4 pb-24">
        {/* Info banner */}
        {!loading && pros.length > 0 && (
          <div className="bg-blue-pale rounded-2xl p-4 mb-4 flex items-start gap-3">
            <span className="text-xl">💡</span>
            <div>
              <div className="font-bold text-navy text-sm mb-0.5">First come, first served</div>
              <div className="text-xs text-gray-500">These pros were ranked by rating, response time, and proximity to you.</div>
            </div>
          </div>
        )}

        {/* Loading skeletons */}
        {loading && (
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-2xl p-4">
                <div className="flex gap-3 mb-3">
                  <div className="skeleton w-16 h-16 rounded-2xl flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-4 w-1/2 rounded"></div>
                    <div className="skeleton h-3 w-1/3 rounded"></div>
                    <div className="skeleton h-3 w-2/3 rounded"></div>
                  </div>
                </div>
                <div className="skeleton h-3 w-full rounded mb-1"></div>
                <div className="skeleton h-3 w-3/4 rounded mb-3"></div>
                <div className="flex gap-2">
                  <div className="skeleton flex-1 h-10 rounded-full"></div>
                  <div className="skeleton flex-1 h-10 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty / expired */}
        {!loading && pros.length === 0 && (
          <div className="text-center py-16 animate-fade-in">
            <div className="text-5xl mb-4">{expired ? "⏰" : "😕"}</div>
            <div className="font-bold text-navy text-lg mb-2">
              {expired ? "Time's up!" : "No pros available"}
            </div>
            <div className="text-sm text-gray-400 mb-6">
              {expired ? "This selection window has expired. We'll find new pros for you." : "We couldn't find available pros right now."}
            </div>
            <button onClick={() => navigate("/explore")} className="btn btn-primary">
              Browse All Pros
            </button>
          </div>
        )}

        {/* Pro cards */}
        {!loading && pros.map((pro, i) => (
          <div key={pro.bookingId}
            className={"bg-white rounded-2xl shadow-card mb-4 overflow-hidden animate-fade-in " +
              (i === 0 ? "ring-2 ring-mouna-blue ring-offset-2" : "")}>

            {/* Top badge */}
            {i === 0 && (
              <div className="bg-gradient-to-r from-mouna-blue to-blue-light px-4 py-2 flex items-center gap-2">
                <span className="text-white text-xs font-bold">⭐ Top Match</span>
                <span className="text-blue-200 text-xs">Highest ranked for your job</span>
              </div>
            )}

            <div className="p-4">
              {/* Pro info */}
              <div className="flex items-start gap-3 mb-3">
                <div className="w-16 h-16 rounded-2xl overflow-hidden bg-blue-pale flex-shrink-0 relative">
                  {pro.proPhoto
                    ? <img src={pro.proPhoto} alt={pro.proName} className="w-full h-full object-cover"/>
                    : <div className="w-full h-full flex items-center justify-center text-3xl">👷</div>}
                  {pro.isOnline && (
                    <div className="absolute bottom-1 right-1 w-3 h-3 bg-teal rounded-full border-2 border-white"></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-navy">{pro.proName}</div>
                  <div className="text-xs text-mouna-blue font-semibold">{pro.serviceCategory}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-gray-500">⭐ {pro.proRating || "New"}</span>
                    {pro.city && <span className="text-xs text-gray-500">📍 {pro.city}</span>}
                    {pro.hourlyRate > 0 && <span className="text-xs font-bold text-navy">${pro.hourlyRate}/hr</span>}
                  </div>
                  {(pro.hireCount || pro.acceptanceRate) ? (
                    <div className="flex gap-2 mt-1">
                      {pro.hireCount ? <span className="text-xs text-gray-400">✅ {pro.hireCount} hires</span> : null}
                      {pro.acceptanceRate ? <span className="text-xs text-gray-400">⚡ {pro.acceptanceRate}% accept</span> : null}
                    </div>
                  ) : null}
                </div>
              </div>

              {pro.bio && (
                <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">{pro.bio}</p>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <button onClick={() => navigate(`/pro/${pro.proId}`)}
                  className="flex-1 btn btn-secondary py-2.5 text-sm">
                  View Profile
                </button>
                <button onClick={() => handleSelect(pro.bookingId, pro.proName)}
                  disabled={selecting !== null}
                  className={"flex-1 btn py-2.5 text-sm font-bold text-white " +
                    (i === 0 ? "bg-mouna-blue" : "bg-navy")}>
                  {selecting === pro.bookingId ? "Selecting..." : "Choose Pro →"}
                </button>
              </div>
            </div>
          </div>
        ))}

        {!loading && pros.length > 0 && (
          <div className="bg-white rounded-2xl p-4 shadow-card text-center">
            <div className="text-xs text-gray-500 mb-2">Not happy with these matches?</div>
            <button onClick={() => navigate("/explore")}
              className="text-mouna-blue font-bold text-sm">Browse all pros →</button>
          </div>
        )}
      </div>
    </div>
  );
}
